/**
 * PurgeConfirmModal Component
 *
 * A dangerous action confirmation modal that requires the user to type
 * the tenant slug to confirm permanent deletion.
 *
 * @module features/tenants/components/modals/PurgeConfirmModal
 */

import { AlertIcon, RefreshIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';

interface PurgeConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The tenant to be purged */
  tenant: Tenant | null;
  /** Callback to close/cancel the modal */
  onClose: () => void;
  /** Callback to execute the purge */
  onConfirm: () => void;
  /** Current slug confirmation input value */
  slugConfirmation: string;
  /** Callback when slug confirmation changes */
  onSlugChange: (value: string) => void;
  /** Whether purge is in progress */
  isPurging: boolean;
  /** Error message to display */
  error: string | null;
  /** Callback to clear error */
  onClearError: () => void;
}

/**
 * Renders a dangerous action confirmation modal requiring slug confirmation.
 */
export const PurgeConfirmModal: React.FC<PurgeConfirmModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onConfirm,
  slugConfirmation,
  onSlugChange,
  isPurging,
  error,
  onClearError,
}) => {
  if (!isOpen || !tenant) {
    return null;
  }

  const slugMatches = slugConfirmation === tenant.slug;
  const charsRemaining = tenant.slug.length - slugConfirmation.length;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        className="glass-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          width: '90%',
          padding: '24px',
          animation: 'fadeIn 0.2s ease-out'
        }}
      >
        {/* Header */}
        <h2 style={{
          color: 'var(--error)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '20px'
        }}>
          <AlertIcon size={24} />
          Permanently Delete Tenant
        </h2>

        {/* Content */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ marginBottom: '12px' }}>
            You are about to <strong style={{ color: 'var(--error)' }}>permanently delete</strong> the tenant:
          </p>

          {/* Tenant Info Box */}
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{ fontWeight: 600, fontSize: '16px' }}>{tenant.name}</div>
            <code style={{
              fontSize: '13px',
              opacity: 0.7,
              fontFamily: 'monospace'
            }}>{tenant.slug}</code>
          </div>

          {/* Warning Box */}
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.5)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{
              fontWeight: 600,
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              WARNING: This action is IRREVERSIBLE
            </div>
            <ul style={{
              margin: '0',
              paddingLeft: '20px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              <li>All tenant data will be permanently destroyed</li>
              <li>All users will lose access immediately</li>
              <li>Any associated schema will be dropped</li>
              <li>Audit logs for this tenant will be deleted</li>
              <li><strong>This cannot be undone</strong></li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px'
            }}>
              To confirm deletion, type the tenant slug:{' '}
              <code style={{
                background: 'rgba(0,0,0,0.4)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                userSelect: 'all'
              }}>{tenant.slug}</code>
            </label>

            <input
              type="text"
              className="input"
              placeholder={`Type "${tenant.slug}" to confirm`}
              value={slugConfirmation}
              onChange={(e) => {
                onSlugChange(e.target.value);
                if (error) onClearError();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && slugMatches) {
                  onConfirm();
                }
              }}
              style={{
                width: '100%',
                fontFamily: 'monospace'
              }}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />

            {/* Slug Match Indicator */}
            {slugConfirmation && (
              <div style={{
                marginTop: '8px',
                fontSize: '13px',
                color: slugMatches ? 'var(--success)' : 'var(--error)'
              }}>
                {slugMatches
                  ? '✓ Slug matches - deletion enabled'
                  : `✗ Slug does not match (${charsRemaining > 0 ? `${charsRemaining} more characters` : 'check spelling'})`
                }
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                background: 'rgba(239, 68, 68, 0.2)',
                borderRadius: '4px',
                color: 'var(--error)',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: '16px',
          marginTop: '8px'
        }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ minWidth: '100px' }}
          >
            Cancel
          </button>
          <button
            className="btn btn-error"
            onClick={onConfirm}
            disabled={!slugMatches || isPurging}
            style={{
              minWidth: '160px',
              opacity: slugMatches ? 1 : 0.5,
              cursor: slugMatches ? 'pointer' : 'not-allowed'
            }}
          >
            {isPurging ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RefreshIcon size={14} className="spin" />
                Deleting...
              </span>
            ) : (
              'Permanently Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
